// src/encounter/lib/to-invoice-section.ts
// Map an `InvoiceDetail` to the Wrap-Up Invoice section's view model.
//
// FLUTTER SOURCE — `invoice_services_widget.dart` (the widget the wrap-up screen mounts at
// wrap_up_billing_screen.dart:818). Andrew's live screenshots 2026-08-06 (self-pay + insurance)
// are the visual anchor; every rule below is from the .dart, not inferred from the pixels:
//   · diagnosisWidget()   :157-200  — "DIAGNOSIS" + a 4-up wrap (2-up under 600px) of
//                                     [index] title / code•type. Empty → "No diagnosis available".
//   · procedureHeader()   :257-300+ — the column set, WIDENED for a sponsored invoice.
//   · proceduresWidget()  :202-233  — numbered rows; empty →
//                                     "No procedure added, add procedure by pressing + icon."
//   · procedureDetail()   :789-890+ — the totals panel and its conditional rows.
//
// SPONSOR BRANCHING is the part a pixel-only port gets wrong — Flutter keys it off
// `sponsorType` against the app constants (app_constants.dart:727-729):
//   "Self-pay" | "Insurance" | "Corporate".
// Note the two conditions are NOT the same test: the extra COLUMNS and the Patient-Share /
// Excluded total rows appear for ANY non-self-pay sponsor (so Corporate too), while
// "Sponsor Amount Total" and the SPONSOR SHARE column are Insurance-ONLY (:294-300, :841).
import type { InvoiceDetail } from '@/shared/types/billing';

/** Flutter `paymentType*` constants (app_constants.dart:727-729) — compared case-insensitively
 *  here because the wire's casing is not guaranteed; Flutter compares the literal. */
export const SPONSOR_TYPE_SELF_PAY = 'Self-pay';
export const SPONSOR_TYPE_INSURANCE = 'Insurance';

/** Flutter coverage options (app_constants.dart:753-755). 'Needs approval' is INSURANCE-only
 *  (invoice_services_widget.dart:97-106). */
export const COVERAGE_INCLUDED = 'Included';
export const COVERAGE_EXCLUDED = 'Excluded';
export const COVERAGE_NEEDS_APPROVAL = 'Needs approval';
export type InvoiceCoverage =
  | typeof COVERAGE_INCLUDED
  | typeof COVERAGE_EXCLUDED
  | typeof COVERAGE_NEEDS_APPROVAL;

export interface InvoiceDxRow {
  /** 1-based display index (Flutter `"${++index}"`). */
  index: number;
  /** Diagnosis text — wire field is `conditionCode.display`, NOT `displayName`. */
  title: string;
  /** Flutter `codeAndType()` → "S32.464K • ICD-10". */
  description: string;
}

export interface InvoiceServiceRow {
  /** 1-based display index. */
  index: number;
  /** Invoice-LINE id (the value `removeServices` takes). */
  id: string;
  /** Procedure display name. */
  label: string;
  /** "3510F • CPT" style sub-label. */
  codeLine?: string;
  /** Third sub-line in the screenshots ("Consultation" / "Drug") — the billing group. */
  groupLine?: string;
  quantity: number;
  gross: number;
  discount: number;
  /** Saved per-line discount reason — seeds the Edit (discount) dialog's reason picker. */
  discountReason?: string;
  net: number;
  patientShare: number;
  sponsorShare: number;
  excluded: boolean;
  /** Current coverage selection — Flutter `selectedCoverageOption()`. `isApproval` wins over
   *  `isExcluded` because a line awaiting approval is not yet an exclusion. */
  coverage: InvoiceCoverage;
  authCode?: string;
  serviceDate?: string;
  /** "start – end" (or the single date when only one bound is set); undefined when neither is. */
  validityPeriod?: string;
  /** Raw ISO bounds — the pickers seed from these rather than re-parsing `validityPeriod`. */
  validityStart?: string;
  validityEnd?: string;
}

export interface InvoiceTotalRow {
  label: string;
  amount: number;
  /** Patient Payable is the one emphasised row (Flutter renders it at 17px, others 13px). */
  emphasis?: boolean;
}

export interface InvoiceSectionData {
  /** Non-self-pay (Insurance OR Corporate) — widens the table and the totals. */
  sponsored: boolean;
  /** Insurance specifically — adds SPONSOR SHARE + Sponsor Amount Total. */
  insurance: boolean;
  /** Invoice is a draft — Flutter gates the Add-Service affordance on this. */
  draft: boolean;
  currencyCode: string;
  dx: InvoiceDxRow[];
  rows: InvoiceServiceRow[];
  totals: InvoiceTotalRow[];
  /** The invoice's own identity, for the block's one meta line. Each part only when the invoice has it. */
  meta?: { number?: string; date?: string; status?: string };
}

const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Flutter `codeAndType()` — "{code} • {system}", collapsing to whichever part exists. */
function codeAndType(code?: string, system?: string): string {
  return [code?.trim(), system?.trim()].filter(Boolean).join(' • ');
}

/** VALIDITY PERIOD cell. Flutter edits it as a range whose end defaults to the start
 *  (`updateValidity.validityEndDate = service.validityEndDate ?? service.validityStartDate`,
 *  wrap_up_billing_screen.dart:925-931), so a start-only line shows the single date. */
function validityPeriod(start?: string | null, end?: string | null): string | undefined {
  const s = start?.trim();
  const e = end?.trim();
  if (!s && !e) return undefined;
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || undefined;
}

export function toInvoiceSection(
  invoice: InvoiceDetail | null | undefined,
  currencyCode = 'AED',
): InvoiceSectionData | null {
  // Flutter: `if (isBlank(invoice)) return Text("Invoice not created")` (wrap_up_billing_screen:777).
  // Null here is that state — the section renders the same not-created copy.
  if (!invoice) return null;

  const sponsorType = invoice.sponsorType?.trim() ?? '';
  const sponsored = sponsorType.toLowerCase() !== SPONSOR_TYPE_SELF_PAY.toLowerCase() && sponsorType !== '';
  const insurance = sponsorType.toLowerCase() === SPONSOR_TYPE_INSURANCE.toLowerCase();
  const draft = (invoice.invoiceStatus ?? '').toLowerCase() === 'draft';

  // Dx rows: `assessmentPlan.assessmentPlanCondition[]`, NOT the `diagnosisList` wire field —
  // that one ships an AssessmentPlan-shaped array of nested meds/procedures (see the type's
  // own warning). Soft-deleted conditions are dropped.
  const dx: InvoiceDxRow[] = (invoice.assessmentPlan?.assessmentPlanCondition ?? [])
    .filter((c) => !c.isDeleted)
    .map((c, i) => ({
      index: i + 1,
      title: c.conditionCode?.display?.trim() || c.conditionCode?.code?.trim() || 'Diagnosis',
      description: codeAndType(c.conditionCode?.code, c.conditionCode?.system),
    }));

  // Service rows. `grossAmount ?? fee` and `discountAmount ?? discount` per the wire notes on
  // InvoiceService — the aliased older names are often absent on invoice-GET rows.
  const rows: InvoiceServiceRow[] = (invoice.services ?? [])
    .filter((s): s is typeof s & { id: string } => !!s.id)
    .map((s, i) => {
      const gross = n(s.grossAmount ?? s.fee);
      const discount = n(s.discountAmount ?? s.discount);
      return {
        index: i + 1,
        id: s.id,
        label: s.procedureCode?.displayName?.trim() || s.procedureCode?.code?.trim() || 'Service',
        codeLine: codeAndType(s.procedureCode?.code, s.procedureCode?.system),
        // The screenshots' third sub-line ("Consultation" / "Drug") — BillingGroup exposes it
        // as `category` (there is no `name` on that type).
        groupLine: s.billingGroup?.category?.trim() || undefined,
        quantity: n(s.quantity) || 1,
        gross,
        discount,
        discountReason: s.discountReason?.trim() || undefined,
        net: n(s.netAmount ?? gross - discount),
        // patientAmount is preferred over patientShare — the latter is occasionally null on
        // draft rows (see the InvoiceService field note).
        patientShare: n(s.patientAmount ?? s.patientShare),
        sponsorShare: n(s.sponsorAmount),
        excluded: !!s.isExcluded,
        coverage: s.isApproval
          ? COVERAGE_NEEDS_APPROVAL
          : s.isExcluded
            ? COVERAGE_EXCLUDED
            : COVERAGE_INCLUDED,
        authCode: s.authCode ?? s.authorizationCode ?? undefined,
        serviceDate: s.serviceAddedDate ?? undefined,
        validityPeriod: validityPeriod(s.validityStartDate, s.validityEndDate),
        validityStart: s.validityStartDate ?? undefined,
        validityEnd: s.validityEndDate ?? undefined,
      };
    });

  // Totals — order and conditions verbatim from procedureDetail() (:789-890).
  const managementDiscount = n(invoice.managementDiscount);
  const totals: InvoiceTotalRow[] = [
    { label: 'Gross Total', amount: n(invoice.grossTotal) },
    { label: 'Discount Total', amount: n(invoice.discountTotal) },
    { label: 'Net Total', amount: n(invoice.netTotal) },
  ];
  if (sponsored) {
    totals.push({ label: 'Patient Share Total', amount: n(invoice.patientShareTotal) });
    totals.push({ label: 'Excluded Total', amount: n(invoice.excludedTotal) });
    if (insurance) {
      totals.push({ label: 'Sponsor Amount Total', amount: n(invoice.sponsorAmountTotal) });
    }
  }
  // Self-pay only, and only once a management discount actually exists (:852-862).
  if (!sponsored && managementDiscount > 0) {
    totals.push({ label: 'Management Discount', amount: managementDiscount });
  }
  totals.push({ label: 'Patient Payable', amount: n(invoice.patientPayable), emphasis: true });
  totals.push({ label: 'Deposited', amount: n(invoice.deposited) });
  // Flutter `displayOutStanding()` reads patientOutstanding (invoice_main_model.dart:463-465) —
  // NOT sponsorOutstanding; this panel is the patient-facing one.
  totals.push({ label: 'Outstanding', amount: n(invoice.patientOutstanding) });

  const meta = {
    number: invoice.invoiceNumber?.trim() || undefined,
    date: invoice.invoiceDate?.trim() || undefined,
    status: invoice.invoiceStatus?.trim() || undefined,
  };
  return { sponsored, insurance, draft, currencyCode, dx, rows, totals, meta };
}

/** Which totals the Invoice block shows: the rows main showed (same order and conditions), with two Practice-app rules
 *  (PM `src/scheduling/components/appointments/sections/payment-section.tsx:262-270`, BreakdownCard): Patient Payable only
 *  for Insurance and Corporate (not Self-pay), Sponsor Amount Total only for Insurance. Excluded Total only above zero.
 *  The emphasised total: Patient Payable when shown; for Self-pay, Outstanding (the amount the patient still owes).
 *  Display only: the amounts are the invoice's own, and a row the data does not hold is never invented. */
export function invoiceTotalRows(data: Pick<InvoiceSectionData, 'sponsored' | 'insurance' | 'totals'>): InvoiceTotalRow[] {
  return data.totals
    .filter((t) => {
      if (t.label === 'Excluded Total' && t.amount <= 0) return false;
      if (t.label === 'Patient Payable' && !data.sponsored) return false;
      if (t.label === 'Sponsor Amount Total' && !data.insurance) return false;
      return true;
    })
    .map((t) => ({ ...t, emphasis: data.sponsored ? !!t.emphasis : t.label === 'Outstanding' }));
}

/** Flutter money display: "{code} {value.toFixed(2)}" (invoice_main_model.dart:451-465). */
export function formatMoney(amount: number, currencyCode: string): string {
  return `${currencyCode} ${amount.toFixed(2)}`;
}
